// Phase 3.7 (beads hh-qbg) — OutOfCatalogScreen.
//
// case-45 flow: equipment that is NOT in the catalog. A short stepped
// "AI-interview" (3-5 questions: name, type, usage frequency, consumables,
// optional instruction photo) collects answers in LOCAL state — no agent
// call in v1. On finish the equipment is created as a DRAFT.
//
// DRAFT HANDLING / LIMITATION:
// The backend Equipment entity (Data/Entities/Equipment.cs) has NO `draft`
// field, and POST /api/v1/equipment accepts no draft marker. As a workaround
// the name is prefixed with "(черновик) " so the draft state is persisted and
// visible everywhere the name is shown. The card renders a draft badge from
// the same prefix. A real `IsDraft` column is a follow-up.
//
// LOCATION: equipment requires a room or property (CHECK constraint
// ck_equipment_location). The "name" step now carries the shared
// EquipmentPlacePicker; when the home has no properties yet, a default one is
// bootstrapped on finish so the draft still saves in one action.
//
// Ported (style + flow) from designproject/files/screens-onboard.jsx
// (`OutOfCatalogScreen`).

import type { JSX } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { T, Icon, Avatar } from '../../tokens';
import { ScreenHeader } from '../../shell';
import type { RouterApi } from '../../router';
import { equipment, properties, humanizeError } from '../../api';
import type { PropertyTreeNode } from '../../api/endpoints/properties.js';
import { FormLabel, FormInput, FormFooter, FormError } from '../forms/formPrimitives';
import { EquipmentPlacePicker } from '../forms/EquipmentPlacePicker';
import type { EquipmentPlaceSelection } from '../forms/EquipmentPlacePicker';

export interface OutOfCatalogScreenProps {
  readonly router: RouterApi;
}

/** Prefix marking an equipment record as a draft (see file header). */
export const DRAFT_PREFIX = '(черновик) ';

interface Step {
  readonly key: string;
  readonly question: string;
  readonly help: string;
  readonly options: readonly string[];
}

const STEPS: readonly Step[] = [
  {
    key: 'kind',
    question: 'Что это за оборудование?',
    help: 'Коротко опишу тип — это поможет подобрать сроки.',
    options: ['Бытовая техника', 'Инструмент', 'Садовая техника', 'Другое'],
  },
  {
    key: 'usage',
    question: 'Как часто пользуешься?',
    help: 'Влияет на интервал между чистками и проверками.',
    options: ['Каждый день', 'Несколько раз в неделю', 'Раз в неделю', 'Реже'],
  },
  {
    key: 'care',
    question: 'Как часто чистить или обслуживать?',
    help: 'Я добавлю это в напоминания.',
    options: ['Раз в месяц', 'Раз в 3 месяца', 'Раз в полгода', 'Раз в год'],
  },
];

function describeError(err: unknown): string {
  return humanizeError(err, 'save');
}

export function OutOfCatalogScreen({ router }: OutOfCatalogScreenProps): JSX.Element {
  // Phase 0 = name entry, 1..N = interview steps, N+1 = photo step.
  const [name, setName] = useState<string>('');
  const [phase, setPhase] = useState<'name' | 'interview' | 'photo'>('name');
  const [stepIndex, setStepIndex] = useState<number>(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [pending, setPending] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Location picker — equipment must land in a room or a property.
  const [propertyTree, setPropertyTree] = useState<readonly PropertyTreeNode[]>([]);
  const [locStatus, setLocStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [locError, setLocError] = useState<string | null>(null);
  const [selectedLoc, setSelectedLoc] = useState<EquipmentPlaceSelection | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLocStatus('loading');
    properties
      .getPropertyTree()
      .then((tree) => {
        if (cancelled) return;
        setPropertyTree(tree.properties);
        setLocStatus('ready');
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setLocError(humanizeError(err, 'load'));
        setLocStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const totalSteps = STEPS.length + 1; // interview steps + photo
  const progressIndex =
    phase === 'name' ? 0 : phase === 'photo' ? totalSteps : stepIndex + 1;

  const back = (): void => {
    if (phase === 'name') {
      router.back();
      return;
    }
    if (phase === 'interview' && stepIndex === 0) {
      setPhase('name');
      return;
    }
    if (phase === 'interview') {
      setStepIndex(stepIndex - 1);
      return;
    }
    // photo → last interview step
    setPhase('interview');
    setStepIndex(STEPS.length - 1);
  };

  const advance = (): void => {
    if (phase === 'name') {
      setPhase('interview');
      setStepIndex(0);
      return;
    }
    if (phase === 'interview') {
      if (stepIndex + 1 < STEPS.length) {
        setStepIndex(stepIndex + 1);
      } else {
        setPhase('photo');
      }
    }
  };

  const pickAnswer = (step: Step, option: string): void => {
    setAnswers((prev) => ({ ...prev, [step.key]: option }));
    advance();
  };

  const finish = (): void => {
    if (name.trim().length === 0) return;
    setPending(true);
    setError(null);
    // Collected interview answers go into the description so they survive the
    // round-trip even though there is no structured field for them yet.
    const notes = STEPS.map((s) => {
      const a = answers[s.key];
      return a !== undefined ? `${s.question} ${a}` : null;
    }).filter((x): x is string => x !== null);
    // Equipment must have a location. When the home has no properties yet,
    // bootstrap a default one so the wizard still completes in one action.
    const locate = async (): Promise<{ roomId?: string; propertyId?: string }> => {
      if (selectedLoc?.kind === 'room') return { roomId: selectedLoc.id };
      if (selectedLoc?.kind === 'property') return { propertyId: selectedLoc.id };
      const created = await properties.createProperty({ name: 'Мой дом' });
      return { propertyId: created.id };
    };
    locate()
      .then((location) =>
        equipment.addEquipment({
          name: `${DRAFT_PREFIX}${name.trim()}`,
          category: 'other',
          ...(notes.length > 0 ? { model: notes.join('; ') } : {}),
          ...location,
        }),
      )
      .then((created) => {
        router.go('eq_card', { id: created.id, draft: true });
      })
      .catch((err: unknown) => {
        setError(describeError(err));
        setPending(false);
      });
  };

  const currentStep: Step | undefined = STEPS[stepIndex];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: T.bg }}>
      <ScreenHeader
        title={name.trim().length > 0 ? name.trim() : 'Новое оборудование'}
        subtitle={
          phase === 'name'
            ? 'Нет в каталоге — заполним черновик'
            : `Уточняю детали · ${progressIndex} из ${totalSteps}`
        }
        onBack={back}
      />

      {/* Origin breadcrumb */}
      <div style={{ padding: '0 16px 8px' }}>
        <div
          style={{
            background: T.brandSoft,
            border: `1px solid ${T.brandSoft2}`,
            borderRadius: 10,
            padding: '8px 12px',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}
        >
          <Icon name="spark" size={14} color={T.brandDeep} />
          <div style={{ flex: 1, fontSize: 11.5, color: T.brandDeep, fontWeight: 600, lineHeight: 1.3 }}>
            Этого нет в каталоге — соберём черновик за минуту
          </div>
        </div>
      </div>

      {/* Progress bar (interview + photo steps) */}
      {phase !== 'name' && (
        <div style={{ padding: '0 16px 16px', display: 'flex', gap: 4 }}>
          {Array.from({ length: totalSteps }, (_, i) => (
            <div
              key={i}
              style={{
                flex: 1,
                height: 3,
                borderRadius: 2,
                background: i < progressIndex ? T.brand : T.borderStrong,
              }}
            />
          ))}
        </div>
      )}

      <div style={{ flex: 1, overflow: 'auto', padding: '0 16px 16px' }}>
        {phase === 'name' && (
          <>
            <FormLabel style={{ marginTop: 8 }}>Название</FormLabel>
            <FormInput
              value={name}
              onChange={setName}
              placeholder="Например: стиральная машина Bosch"
              disabled={pending}
            />

            <FormLabel style={{ marginTop: 18 }}>Где находится</FormLabel>
            {locStatus === 'loading' && (
              <div style={{ fontSize: 12.5, color: T.ink3, padding: '6px 2px' }}>
                Загружаю помещения…
              </div>
            )}
            {locStatus === 'error' && locError !== null && <FormError message={locError} />}
            {locStatus === 'ready' && propertyTree.length === 0 && (
              <div style={{ fontSize: 12.5, color: T.ink3, padding: '6px 2px', lineHeight: 1.4 }}>
                Помещений пока нет — оборудование попадёт в дом «Мой дом», он создастся
                автоматически.
              </div>
            )}
            {locStatus === 'ready' && propertyTree.length > 0 && (
              <EquipmentPlacePicker
                tree={propertyTree}
                value={selectedLoc}
                onChange={(sel) => {
                  setSelectedLoc(sel);
                  setError(null);
                }}
                disabled={pending}
              />
            )}

            <div style={{ fontSize: 12.5, color: T.ink3, marginTop: 10, lineHeight: 1.4 }}>
              Запишу как черновик — потом можно уточнить регламент.
            </div>
            {error !== null && <FormError message={error} />}
          </>
        )}

        {phase === 'interview' && currentStep !== undefined && (
          <>
            <div
              style={{
                fontSize: 20,
                fontWeight: 700,
                letterSpacing: -0.3,
                color: T.ink,
                lineHeight: 1.25,
                marginTop: 4,
              }}
            >
              {currentStep.question}
            </div>
            <div style={{ fontSize: 13, color: T.ink3, marginTop: 6, lineHeight: 1.4 }}>
              {currentStep.help}
            </div>
            <div style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {currentStep.options.map((option) => {
                const selected = answers[currentStep.key] === option;
                return (
                  <button
                    key={option}
                    type="button"
                    onClick={() => pickAnswer(currentStep, option)}
                    style={{
                      padding: '14px 16px',
                      background: selected ? T.brandSoft : T.surface,
                      border: `1px solid ${selected ? T.brandSoft2 : T.border}`,
                      borderRadius: 12,
                      fontFamily: T.font,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      textAlign: 'left',
                    }}
                  >
                    <div
                      style={{
                        width: 20,
                        height: 20,
                        borderRadius: 10,
                        border: `2px solid ${selected ? T.brand : T.borderStrong}`,
                        background: selected ? T.brand : 'transparent',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      {selected && <Icon name="check" size={12} color="#fff" strokeWidth={2.5} />}
                    </div>
                    <div style={{ flex: 1, fontSize: 14, fontWeight: 600, color: T.ink }}>{option}</div>
                  </button>
                );
              })}
            </div>
          </>
        )}

        {phase === 'photo' && (
          <>
            <div
              style={{
                fontSize: 20,
                fontWeight: 700,
                letterSpacing: -0.3,
                color: T.ink,
                lineHeight: 1.25,
                marginTop: 4,
              }}
            >
              Есть инструкция?
            </div>
            <div style={{ fontSize: 13, color: T.ink3, marginTop: 6, lineHeight: 1.4 }}>
              Фото страниц про обслуживание учтёт сроки производителя.
            </div>
            <div style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <button
                type="button"
                disabled
                title="Скоро"
                style={{
                  padding: 16,
                  background: T.surface,
                  border: `1.5px dashed ${T.borderStrong}`,
                  borderRadius: 14,
                  fontFamily: T.font,
                  cursor: 'not-allowed',
                  opacity: 0.7,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  textAlign: 'left',
                }}
              >
                <Avatar kind="sys" icon="camera" size={36} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: T.ink }}>Снять инструкцию</div>
                  <div style={{ fontSize: 12, color: T.ink3, marginTop: 2 }}>скоро</div>
                </div>
              </button>
              <div style={{ fontSize: 12.5, color: T.ink3, lineHeight: 1.4 }}>
                Без инструкции — сохраню черновик с базовыми сроками.
              </div>
            </div>
            {error !== null && <FormError message={error} />}
          </>
        )}
      </div>

      {phase === 'name' && (
        <FormFooter
          onCancel={() => router.back()}
          onConfirm={advance}
          confirmLabel="Дальше"
          confirmIcon="chevR"
          disabled={
            name.trim().length === 0 ||
            pending ||
            locStatus !== 'ready' ||
            (propertyTree.length > 0 && selectedLoc === null)
          }
        />
      )}
      {phase === 'interview' && (
        <FormFooter
          onCancel={back}
          onConfirm={advance}
          confirmLabel="Пропустить шаг"
          disabled={pending}
        />
      )}
      {phase === 'photo' && (
        <FormFooter
          onCancel={back}
          onConfirm={finish}
          confirmLabel="Сохранить черновик"
          confirmIcon="check"
          disabled={pending}
          pending={pending}
        />
      )}
    </div>
  );
}
